import { useState, useEffect, useCallback } from 'react';

export const pinLast = (fields) => [
  ...fields.filter((field) => !field.pinnedLast),
  ...fields.filter((field) => field.pinnedLast),
];

const defaultColumns = (columns) => ({
  visible: pinLast(columns.filter((column) => !column.defaultHidden || column.alwaysVisible)),
  hidden: columns.filter((column) => column.defaultHidden && !column.alwaysVisible),
});

const restoreColumns = (columns, saved) => {
  if (!saved || !Array.isArray(saved.visible) || !Array.isArray(saved.hidden)) return defaultColumns(columns);
  const byId = new Map(columns.map((column) => [column.id, column]));
  const seen = new Set();
  const restore = (fields) => fields.reduce((result, field) => {
    const column = field && byId.get(field.id);
    if (column && !seen.has(column.id)) {
      seen.add(column.id);
      result.push(column);
    }
    return result;
  }, []);
  const visible = restore(saved.visible);
  const hidden = restore(saved.hidden);
  if (visible.length === 0) return defaultColumns(columns);
  const added = columns.filter((column) => !seen.has(column.id));
  return {
    visible: pinLast([...visible, ...hidden.filter((column) => column.alwaysVisible), ...added.filter((column) => column.alwaysVisible)]),
    hidden: [...hidden, ...added].filter((column) => !column.alwaysVisible),
  };
};

export default function useTableColumns({ columns, localStorageKey, teamId }) {
  const storageKey = typeof localStorageKey === 'function' ? localStorageKey(teamId) : localStorageKey;
  const [fields, setFields] = useState(() => defaultColumns(columns));

  useEffect(() => {
    let restored = defaultColumns(columns);
    try {
      if (teamId) restored = restoreColumns(columns, JSON.parse(localStorage.getItem(storageKey)));
    } catch (_error) {}
    setFields(restored);
  }, [teamId, storageKey]);

  const handleColumnsChange = useCallback((visible, hidden) => {
    const next = restoreColumns(columns, { visible, hidden });
    setFields(next);
    if (!teamId) return;
    try {
      localStorage.setItem(storageKey, JSON.stringify({
        visible: next.visible.map(({ id }) => ({ id })),
        hidden: next.hidden.map(({ id }) => ({ id })),
      }));
    } catch (_error) {}
  }, [columns, teamId, storageKey]);

  return {
    visibleColumns: fields.visible,
    hiddenColumns: fields.hidden,
    handleColumnsChange,
  };
}
